import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { ImplementationForm } from '@Sage/ssm-web5';
import Loader from 'components/loader';
import { ImplementationsModal } from 'components/modal';
import { SearchBar } from '@Sage/hoi-poi-ui';

import FindImplementation from 'containers/FindImplementation';
import { AuthenticationActions } from 'actions';
import './styles.scss';

function mapStateToProps({ authentication, implementations }) {
    const { canSwitchImpl } = authentication;
    return {
        parameters: implementations.current?.parameters || {},
        currentImpl: implementations.current || {},
        currentLoading: implementations.currentLoading,
        canSwitchImpl,
    };
}

const mapDispatchToProps = (dispatch) => ({
    switchImplementation: bindActionCreators(AuthenticationActions, dispatch).switchImplementation,
});

/**
 * Implementation Selector inside header
 */

// class based component >> functional component
const ImplementationSwitch = ({
    switchImplementation,
    canSwitchImpl,
    currentImpl,
    currentLoading,
    onChangeType,
}) => {
    const navigate = useNavigate();
    const [openChoose, setOpenChoose] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);

    const onImplBtnClick = () => {
        setOpenChoose(true);
        setOpenCreate(false);
    };

    const onCloseModal = () => {
        setOpenChoose(false);
        setOpenCreate(false);
    };

    const onImplChange = () => {
        setOpenChoose(false);
        setOpenCreate(false);
    };

    const onNewImplementation = () => {
        setOpenChoose(false);
        setOpenCreate(true);
    };

    const onCloseImplementationForm = useCallback(
        (implId) => {
            if (implId) {
                setOpenCreate(false);
                switchImplementation(implId).then(() => {
                    navigate('/app/workspace', { replace: true });
                });
            } else {
                setOpenChoose(true);
                setOpenCreate(false);
            }
        },
        [navigate, switchImplementation],
    );

    const renderLoading = useMemo(
        () => (
            <div className="impl-switch-loader-content">
                <Loader size="medium" />
            </div>
        ),
        [],
    );

    if (currentLoading || !currentImpl?.id || !currentImpl?.name) {
        return renderLoading;
    }

    const implementationName = `${currentImpl.id}-${currentImpl.name}`;

    const implName = `${implementationName.toUpperCase()}`;

    return (
        <div className="impl-switch">
            {canSwitchImpl ? (
                <Fragment>
                    {/* Modal to switch implementation */}
                    <ImplementationsModal isOpen={openChoose} closeModal={onCloseModal}>
                        <FindImplementation
                            onChange={onImplChange}
                            isModal
                            isModalVisible={openChoose}
                            onNewImplementation={onNewImplementation}
                        />
                    </ImplementationsModal>

                    {/* Modal to create implementation */}
                    {openCreate && (
                        <ImplementationForm
                            isOpen={openCreate}
                            isModal={true}
                            onClose={onCloseImplementationForm}
                            setIsOpen={setOpenCreate}
                            switchModals={onImplBtnClick}
                        />
                    )}

                    {/* Search implementation */}
                    <div onClick={onImplBtnClick} className="impl-switch__search-field">
                        <SearchBar
                            hideDropdownIndicator={true}
                            isReadOnly={true}
                            onChange={onChangeType}
                            inputValue={implName}
                            size="small"
                            overrides={{
                                Select: {
                                    overrides: {
                                        inputComponents: {
                                            className: 'impl__search-field--container',
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                </Fragment>
            ) : (
                <div className="impl-switch__text">{implName}</div>
            )}
        </div>
    );
};

ImplementationSwitch.propTypes = {
    canSwitchImpl: PropTypes.bool,
    currentImpl: PropTypes.shape({}),
    currentLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImplementationSwitch);
